import { Dialog } from '@base-ui/react';
import type { ReactNode, RefObject } from 'react';
import { cva, cx, type VariantProps } from '@/cva.config';

const modalPanelVariants = cva({
  base: [
    'relative w-full animate-fadeIn overflow-hidden rounded-xl border border-border-active bg-surface shadow-2xl',
  ],
  defaultVariants: { size: 'md' },
  variants: {
    size: {
      lg: 'max-w-lg',
      md: 'max-w-md',
      xl: 'max-w-160',
    },
  },
});

interface ModalProps extends VariantProps<typeof modalPanelVariants> {
  align?: 'center' | 'top';
  children: ReactNode;
  className?: string;
  initialFocus?: RefObject<HTMLElement | null>;
  onClose: () => void;
  open: boolean;
  zIndex?: 'modal' | 'palette';
}

const ModalRoot = ({
  align = 'center',
  children,
  className,
  initialFocus,
  onClose,
  open,
  size,
  zIndex = 'modal',
}: ModalProps) => (
  <Dialog.Root
    onOpenChange={(next) => {
      if (!next) onClose();
    }}
    open={open}
  >
    <Dialog.Portal>
      <Dialog.Backdrop
        className={cx(
          'fixed inset-0 z-60 bg-black/60 backdrop-blur-sm',
          zIndex === 'palette' && 'z-50',
        )}
      />
      <Dialog.Popup
        className={cx(
          'fixed inset-0 z-60 flex items-center justify-center p-4',
          zIndex === 'palette' && 'z-50',
          align === 'top' && 'items-start pt-[10vh]',
        )}
        initialFocus={initialFocus}
      >
        <div className={cx(modalPanelVariants({ size }), className)}>
          {children}
        </div>
      </Dialog.Popup>
    </Dialog.Portal>
  </Dialog.Root>
);

export const Modal = Object.assign(ModalRoot, {
  Close: Dialog.Close,
  Description: Dialog.Description,
  Title: Dialog.Title,
});
